import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { HttpContentWithData } from 'store/APIClient';
import { PROJECT_ID } from 'routes/BudgetModule/config/constants';
import { dispatch } from 'store/store';
import { Project } from 'routes/BudgetModule/shared/useGetProjects';
import { useDeleteInscriptionProjectMutation } from '../../../hooks/inscriptionsProjects';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';

type Props = {
    projectId: number;
    queryClient: QueryClient;
    queryCacheKey: InscriptionQueryKey;
    inscriptionIdColumnName?: keyof InscriptionT;
    inscriptionId: string;
};

/**
 * Refresh triggered when user removes successfully a project from an inscription.
 */
const refreshInscriptionsAfterProjectRemoving = ({
    projectId,
    queryClient,
    queryCacheKey,
    inscriptionIdColumnName: inscriptionIdColumn,
    inscriptionId,
}: Props) => {
    queryClient.setQueryData(
        queryCacheKey,
        (oldInscriptions: HttpContentWithData<InscriptionT[]>) => ({
            ...oldInscriptions,
            data: oldInscriptions.data.map((inscription) => {
                if (
                    inscriptionIdColumn &&
                    inscription[inscriptionIdColumn] === inscriptionId
                ) {
                    return {
                        ...inscription,
                        [PROJECT_ID]: inscription[PROJECT_ID].filter(
                            (id) => id !== projectId,
                        ),
                    };
                }
                return inscription;
            }),
        }),
    );
};

/**
 * Custom hook that returns a method that :
 * 1) remove a project-inscription association,
 * 2) then triggers a (manual) refresh of cached inscriptions list.
 */
export const useRemoveProjectFromInscription = (
    inscriptionId: string,
    queryCacheKey: InscriptionQueryKey,
    inscriptionIdColumnName?: keyof InscriptionT,
) => {
    const { mutate: deleteInscriptionProject } =
        useDeleteInscriptionProjectMutation();
    const queryClient = useQueryClient();

    const removeProjectFromInscription = (project: Project) => {
        deleteInscriptionProject(
            {
                projectId: project.id!,
                inscriptionId,
            },
            {
                onSuccess: (response) => {
                    dispatch.vision.notifyUser({
                        message: response.content.message,
                        snackType: 'success',
                    });
                    refreshInscriptionsAfterProjectRemoving({
                        projectId: project.id!,
                        queryClient,
                        queryCacheKey,
                        inscriptionIdColumnName,
                        inscriptionId,
                    });
                },
                onError: (error: Error) => {
                    dispatch.vision.notifyUser({
                        message: error.message,
                        snackType: 'error',
                    });
                },
            },
        );
    };

    return { removeProjectFromInscription };
};
